import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {bindActionCreators} from 'redux';
import clsx from 'clsx';
import ListingBranding from './ListingBranding';
import ListingBannerProps from './ListingBannerComponent';
import TicketsGoOnSale from './TicketsGoOnSale';
import { getMainEvent } from '../../checkout/selectors/event';
import {
  getAltText,
  getTime,
  getLocation,
} from '@h/listingBannerHelpers';
import SalesPaused from "./SalesPaused";
import { getCmsMode, getSeriesConfig, selectIsHeroLayout, selectIsPosterLayout } from '@b/checkout/selectors/ui';
import { StickyEvent } from './StickyEvent';
import { getSeriesOption } from '@v/series/selectors/eventSeries';
import { ListingBrandedHeader } from './ListingBrandedHeader';
import { isListingCheckoutLoginEnabled } from "@/selectors/settings";
import { TICKETS_DISPLAY_LIST } from '@b/checkout/reducers/ui';
import { isDynamicListing } from '@b/checkout/selectors/dynamic-listing';
import { setCheckoutOverlay,  } from '@/actions/ui';
import MemberOnlyTicketsDeleted from "./MemberOnlyTicketsDeleted";
import ListingEventDetails from './ListingEventDetails';

class ListingBanner extends Component<ListingBannerProps> {
    constructor() {
      super();

      this.state = {
        isStickyEventVisible: false,
        ticketsContanerRightOffset: 0,
        isStickyEventAllowed: false,
      }

      this.ticketsContainerRef = createRef();
      this.bannerRef = createRef();
    }

    componentDidMount() {
      this.intersectionObserver = new IntersectionObserver((entries) => {
        entries.forEach(() => {
          if (!this.ticketsContainerRef?.current) {
            this.setState({
              isStickyEventVisible: window.scrollY > this.bannerRef?.current?.offsetHeight
            }, () => this.onWindowResize());
            return;
          }

          const ticketsContainerRect = this.ticketsContainerRef.current.getBoundingClientRect();

          const isBelow = ticketsContainerRect.bottom < 0;

          this.setState({
            isStickyEventVisible: isBelow
          }, () => this.onWindowResize());
        });
      })

      if (this.ticketsContainerRef?.current) {
        this.intersectionObserver.observe(this.ticketsContainerRef.current);
      }

      if (this.props.isHeroLayout) {
        window.top.addEventListener('load', this.createOnWindowLoad());
      }
    }

    componentDidUpdate(prevProps) {
      if (!prevProps.isHeroLayout && this.props.isHeroLayout) {
        this.createOnWindowLoad(true)();
        this.allowStickyEvent();
      }
    }

    componentWillUnmount() {
      this.intersectionObserver.disconnect();
      window.top.removeEventListener('resize', this.onWindowResize);
      window.top.removeEventListener('load', this.createOnWindowLoad());
      window.top.removeEventListener('scroll', this.onScroll);
    }

    onScroll = () => {
      const target = document.documentElement || document.body;

      if (target.scrollTop === 0) {
        this.setState({
          isStickyEventVisible: false,
        })
      }
    }

    allowStickyEvent() {
      this.setState({ isStickyEventAllowed: true });
      this.onWindowResize();
    }

    addBannerImageOnLoadListener = () => {
      const banner = document.getElementById('banner');
      const style = window.getComputedStyle(banner, ':after');

      if (style.backgroundImage === 'none') {
        this.allowStickyEvent();
        return;
      }

      const url = style.backgroundImage.match(/\((.*?)\)/)[1].replace(/('|")/g,'');

      const img = new Image();
      img.onload = () => {
        this.allowStickyEvent();
      }
      img.src = url;
      if (img.complete) img.onload();
    }

    createOnWindowLoad = (synthetic = false) => () => {
      window.top.addEventListener('resize', this.onWindowResize);
      window.top.addEventListener('scroll', this.onScroll);
      if (this.props.cmsMode) {
        !synthetic && this.addBannerImageOnLoadListener();
      } else {
        this.allowStickyEvent();
      }
      this.onWindowResize();
    }

    onWindowResize = () => {
      if (!this.ticketsContainerRef?.current) return;

      setTimeout(() => {
        const rect = this.ticketsContainerRef.current.getBoundingClientRect();
        this.setState({ ticketsContanerRightOffset: window.innerWidth - rect.right });
      });
    }

    renderBannerImage = () => {
      const headerContainerStyle = this.props.headerImageMaxWidth && +this.props.headerImageMaxWidth > 0
            ? {maxWidth: this.props.headerImageMaxWidth + 'px'}
            : {maxWidth: 'none'};

      const bannerImageClassName = clsx('banner__logo-img', {
        'banner__logo-img--full-width': !this.props.isPro,
      });

      return (
        <div ref={this.bannerRef} id="banner" className="banner">
          <div className="banner__logo-img-container" style={headerContainerStyle}>
            {this.props.headerImage !== "" && <div className="banner__logo">
              <img id='banner__logo-img' className={bannerImageClassName} alt={getAltText(this.props.name, this.props.uiConfig)} src={this.props.headerImage} />
            </div>}
          </div>
          {this.props.isDynamic && (
            <button className="banner__button--close" onClick={() => this.props.setCheckoutOverlay(false)}>
              <i className="icon-close" aria-hidden="true"></i>
              <span className="sr-only">Close</span>
            </button>
          )}
        </div>
      )
    }

    render() {
        const shouldUseThemeDark = this.props.uiConfig.styles["BANNER__reverse-text"];
        const pageBannerClassName = clsx('page__banner banner__main', {
          'theme--dark': shouldUseThemeDark,
          'page__banner--inline-tickets': this.props.uiConfig.listings?.ticketsDisplayType === TICKETS_DISPLAY_LIST,
        });

        const isTicketsOnSale = !this.props.isCanceled && !this.props.isPaused && (this.props.invitationCouponApplied || this.props.isOnsale || (!this.props.isOnsale && this.props.hasPresale));

        const shouldRenderStickyEvent =
          !this.props.isDynamic &&
          isTicketsOnSale &&
          this.props.isHeroLayout &&
          this.props.uiConfig.listings?.ticketsDisplayType !== TICKETS_DISPLAY_LIST &&
          this.state.isStickyEventAllowed;

        if (this.props.bannerImageOnly) {
          return (
            <div className="page__banner banner__main">
              {this.renderBannerImage()}
            </div>
          );
        }

        const listingDetails = this.props.isDynamic
          || (this.props.uiConfig.listings?.ticketsDisplayType === TICKETS_DISPLAY_LIST && (this.props.isHeroLayout || this.props.isPosterLayout))
            ? '' : (<ListingEventDetails />);
        
        const ticketsGoOnSale = this.props.isDynamic
          || (this.props.uiConfig.listings?.ticketsDisplayType === TICKETS_DISPLAY_LIST && (this.props.isHeroLayout || this.props.isPosterLayout))
            ? '' : (<div className="event-details__sale-status-container"><TicketsGoOnSale /></div>);
        
        const salesPaused = this.props.isDynamic
          || (this.props.uiConfig.listings?.ticketsDisplayType === TICKETS_DISPLAY_LIST && (this.props.isHeroLayout || this.props.isPosterLayout))
            ? '' : (<div className="event-details__sale-status-container"><SalesPaused /></div>);

        return(
            <>
              <div className={pageBannerClassName} style={{ '--TICKETS-CONTAINER-RIGHT': `${this.state.ticketsContanerRightOffset}px` }}>
                {this.props.isLoginEnabled && <ListingBrandedHeader/>}
                <ListingBranding slug={this.props.slug} brand={this.props.brand}/>
                {this.renderBannerImage()}
                <div className="page__content__main">
                  <div className="page-content__inner">
                    {listingDetails}
                    <div ref={this.ticketsContainerRef} className="tickets-container">
                      {ticketsGoOnSale}
                      {salesPaused}
                      <MemberOnlyTicketsDeleted></MemberOnlyTicketsDeleted>
                      {this.renderTicketsButton()}
                    </div>
                    {shouldRenderStickyEvent && (
                      <StickyEvent
                        show={this.state.isStickyEventAllowed && this.state.isStickyEventVisible}
                        calendarOnListing={this.props.calendarOnListing}
                        name={this.props.name}
                        date={getTime(this.props.uiConfig, this.props.isRecurring, this.props.start, this.props.end, this.props.locale)}
                        ages={!this.props.hideAgeLimit && this.props.ages}
                        location={getLocation(this.props.uiConfig, this.props.venueName, this.props.venueAddress)}
                        image={this.props.headerImage}
                        imageAlt={getAltText(this.props.name, this.props.uiConfig)}
                        TicketsButton={this.props.children}
                      />
                    )}
                  </div>
                </div>
                <div className="banner__background-image">
                </div>
              </div>
            </>
        )
    }

    renderTicketsButton = () => {
        return this.props.children;
    }
}

const mapStateToProps = (state, props) => {
    const venue = state.venue.venues ? Object.values(state.venue.venues)[0] : null;
    const mainEvent = getMainEvent(state);
    const theEvent = mainEvent?.event ?? {};
    return {
        locale: state.intl.locale,
        end: mainEvent?.event?.end ?? '',
        name: mainEvent?.event?.name ?? '',
        start: mainEvent?.event?.start ?? '',
        venueName: venue ? venue.name : "",
        venueAddress: venue ? venue.address.city + ', ' + venue.address.region : "",
        ages: mainEvent?.event?.age_minimum,
        hideAgeLimit: mainEvent?.event?.hide_age_limit,
        slug: state.partner.slug,
        brand: state.partner.brand,
        uiConfig: state.ui.uiConfig,
        headerImage: (state.ui.uiConfig && state.ui.uiConfig.images) ? state.ui.uiConfig.images.header : null,
        headerImageMaxWidth: (state.ui.uiConfig && state.ui.uiConfig.images && state.ui.uiConfig.images.headerImageMaxWidth) ? state.ui.uiConfig.images.headerImageMaxWidth : null,
        isRecurring: !!mainEvent?.event?.isRecurring,
        timezone: mainEvent?.event?.timezone,
        isHeroLayout: selectIsHeroLayout(state),
        isPosterLayout: selectIsPosterLayout(state),
        calendarType: getSeriesConfig(state).calendarType,
        cmsMode: getCmsMode(state),
        calendarOnListing: !!getSeriesOption("calendarOnListing", false)(state),
        isLoginEnabled: isListingCheckoutLoginEnabled(state),
        isOnsale: theEvent.isOnsale,
        isCanceled: theEvent.isCanceled,
        isPaused: theEvent.isPaused,
        inviteCouponApplied: state.couponTag.appliedTaggableInvitationCode || state.reservation.hasInviteCoupon,
        hasPresale: theEvent.hasPresale,
        isDynamic: isDynamicListing(state),
        isPro: state.ui.uiConfig.isPro,
    }
}

const mapDispatchToProps = dispatch => {
    return bindActionCreators({
      setCheckoutOverlay,
    }, dispatch);
}

ListingBanner.defaultProps = {
    headerImage: "",
    isOnsale: false
}

ListingBanner.propTypes = {
    locale: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    slug: PropTypes.string,
    branding: PropTypes.string,
    start: PropTypes.string.isRequired,
    end: PropTypes.string,
    venueName: PropTypes.string.isRequired,
    venueAddress: PropTypes.string.isRequired,
    headerImage: PropTypes.string,
    isRecurring: PropTypes.bool,
    timezone: PropTypes.string,
    setCheckoutOverlay: PropTypes.func
}

export default connect(mapStateToProps, mapDispatchToProps)(ListingBanner)
